import { t } from "@lingui/macro";
import { useState } from "react";
import { Platform } from "react-native";

import { Button } from "@whoosh/design-system/src/components/controls";
import { PoweredByWhooshStacked } from "@whoosh/design-system/src/components/icons_v2/special/PoweredByWhooshStacked";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { getBiometricOptInCopy } from "./getBiometricUnlockCopy";
import { useBiometricUnlock } from "./useBiometricUnlock";

type BiometricOptInModalProps = {
  email?: string | null;
  userId?: string | null;
};

export const BiometricOptInModal = ({
  email,
  userId,
}: BiometricOptInModalProps) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const {
    errorMessage,
    declinePrompt,
    enrolledTypes,
    hasDeclinedPrompt,
    isAvailable,
    isEnabled,
    isLoading,
    setEnabled,
  } = useBiometricUnlock(userId);

  if (
    Platform.OS === "web" ||
    isLoading ||
    isDismissed ||
    !isAvailable ||
    isEnabled ||
    hasDeclinedPrompt ||
    !userId
  ) {
    return null;
  }

  const copy = getBiometricOptInCopy(Platform.OS, enrolledTypes);
  const Glyph = copy.Glyph;

  const enableBiometricUnlock = async () => {
    setIsSubmitting(true);
    const wasEnabled = await setEnabled(true);

    if (wasEnabled) {
      setIsDismissed(true);
      showToast({
        description: copy.toastDescription,
        semanticStyle: "success",
        title: copy.toastTitle,
        toastId: "biometric-unlock-enabled",
      });
    }

    setIsSubmitting(false);
  };

  const declineBiometricUnlock = async () => {
    setIsSubmitting(true);

    try {
      await declinePrompt();
      setIsDismissed(true);
    } catch {
      // Keep the modal open so the user can retry if local persistence fails.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StandardModal
      disableContentScroll
      isOpen
      onClose={() => setIsDismissed(true)}
      subtitle={email ?? undefined}
      upperNode={<PoweredByWhooshStacked width={89} height={31} />}
      upperTitle={copy.title}
      content={
        <VStack flex={1} px={6} testID="biometric-opt-in-modal">
          <Box
            alignItems="center"
            backgroundColor="surface-main-primary-subtlest"
            borderRadius="4"
            height={96}
            justifyContent="center"
            mt={2}
            width={96}
          >
            <Glyph color="icon-main-primary" height={44} width={44} />
          </Box>
          <Text
            color="text-secondary"
            maxWidth={300}
            mt={6}
            variant="mainBaseMedium"
          >
            {copy.benefitCopy}
          </Text>
        </VStack>
      }
      footerContent={
        <VStack gap={2} pb={6}>
          {errorMessage ? (
            <Text variant="mainSmallMedium">{errorMessage}</Text>
          ) : null}
          <Text
            color="text-secondary"
            mb={1}
            textAlign="center"
            variant="mainSmallMedium"
          >
            {t`You can change this later in Profile.`}
          </Text>
          <Button
            accessibilityLabel={copy.primaryCta}
            isLoading={isSubmitting}
            onPress={() => void enableBiometricUnlock()}
            size="regular"
            testID="biometric-opt-in-accept"
            variant="main"
            width="100%"
          >
            {copy.primaryCta}
          </Button>
          <Button
            accessibilityLabel={t`Don't ask again`}
            isDisabled={isSubmitting}
            onPress={() => void declineBiometricUnlock()}
            size="regular"
            testID="biometric-opt-in-decline"
            variant="ghost"
            width="100%"
          >
            {t`Don’t Ask Again`}
          </Button>
        </VStack>
      }
    />
  );
};
